import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import EventForm from '../components/events/EventForm';
import ClassesTab from '../components/events/ClassesTab';
import PackagesTab from '../components/events/PackagesTab';
import RegistrationsTab from '../components/events/RegistrationsTab';
import ConfirmDialog from '../components/shared/ConfirmDialog';
import { getEvent, createEvent, updateEvent, deleteEvent, getTracks, getSanctioningBodies } from '../services/api';
import { features } from '../config/features';
import { getErrorMessage } from '../utils/errors';
import { useAuth } from '../contexts/AuthContext';
import type { RacingEvent, Track, SanctioningBody } from '../types';
import { PageHeader, Alert, Button, Spinner, Tabs, TabsContent, TabsList, TabsTrigger } from '@racing/ui';

// Tabs whose feature flag is off are dropped from the view entirely — see
// config/features.ts. "Details" carries no flag, so the initial `tab` state is
// always a tab that is actually rendered.
const eventTabs: { value: string; label: string }[] = [
  { value: 'details', label: 'Details' },
  ...(features.eventClasses ? [{ value: 'classes', label: 'Classes' }] : []),
  ...(features.eventPackages ? [{ value: 'packages', label: 'Packages' }] : []),
  ...(features.eventRegistrations ? [{ value: 'registrations', label: 'Registrations' }] : []),
];

const EventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const isNew = !id;
  const eventId = id ? parseInt(id, 10) : 0;

  const [event, setEvent] = useState<Partial<RacingEvent>>({
    event_type: 'BRACKET',
    status: 'DRAFT',
  });
  const [tracks, setTracks] = useState<Track[]>([]);
  const [sanctioningBodies, setSanctioningBodies] = useState<SanctioningBody[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tab, setTab] = useState('details');
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Only offer tracks the caller can actually save against — the server
  // refuses an event on a track the user does not hold (authorizeEventTenancy),
  // so listing every track just hands them a guaranteed 403. The event's
  // current track stays in the list regardless, so the field still renders the
  // real value for someone editing an event they reached via another role
  // (e.g. a sanctioning-body user, who holds no tracks at all).
  const selectableTracks = useMemo(() => {
    if (isAdmin) return tracks;
    const held = new Set(user?.trackIds ?? []);
    return tracks.filter((t) => held.has(t.track_id) || t.track_id === event.track_id);
  }, [tracks, isAdmin, user, event.track_id]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const promises: Promise<any>[] = [getTracks(), getSanctioningBodies()];
      if (!isNew) promises.push(getEvent(eventId));
      const results = await Promise.all(promises);
      setTracks(results[0]);
      setSanctioningBodies(results[1]);
      if (!isNew) setEvent(results[2]);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id, isNew, eventId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!id && window.location.pathname !== '/events/new') {
    return <Navigate to="/events" replace />;
  }

  const handleChange = (field: keyof RacingEvent, value: string | number | boolean | null) => {
    setEvent((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      if (isNew) {
        const created = await createEvent(event);
        setSuccess('Event created');
        navigate(`/events/${created.event_id}`, { replace: true });
      } else {
        const updated = await updateEvent(eventId, event);
        setEvent(updated);
        setSuccess('Event saved');
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    try {
      await deleteEvent(eventId);
      navigate('/events');
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center pt-20">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col">
      {/* The back link sits on its own row above the title. Keeping it inline
          with a long event name and both action buttons overflowed the header
          on anything narrower than a desktop. */}
      <Button
        variant="ghost"
        size="sm"
        className="mb-2 -ml-2 self-start"
        onClick={() => navigate('/events')}
      >
        <ArrowLeft /> Back to events
      </Button>

      <PageHeader
        title={isNew ? 'New Event' : event.event_name}
        subtitle={!isNew && event.track_name ? event.track_name : undefined}
        actions={
          <>
            {!isNew && (
              <Button variant="destructive-outline" onClick={handleDeleteClick}>
                <Trash2 /> Delete
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Spinner className="size-4 text-white" label={null} /> : <Save />}
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-4" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {isNew ? (
        <EventForm event={event} tracks={selectableTracks} sanctioningBodies={sanctioningBodies} onChange={handleChange} />
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="min-w-0">
          <TabsList>
            {eventTabs.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="details">
            <EventForm event={event} tracks={selectableTracks} sanctioningBodies={sanctioningBodies} onChange={handleChange} />
          </TabsContent>
          {features.eventClasses && (
            <TabsContent value="classes">
              <ClassesTab eventId={eventId} />
            </TabsContent>
          )}
          {features.eventPackages && (
            <TabsContent value="packages">
              <PackagesTab eventId={eventId} />
            </TabsContent>
          )}
          {features.eventRegistrations && (
            <TabsContent value="registrations">
              <RegistrationsTab eventId={eventId} />
            </TabsContent>
          )}
        </Tabs>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Delete"
        message="Delete this event? This will also delete all classes, packages, and registrations."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default EventDetailPage;
